import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Search, Wand2, Plus, Sparkles } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import NewReservation from "./NewReservation";
import UnassignedPanel from "./UnassignedPanel";
import BookingDetailDialog from "./BookingDetailDialog";

const CH_STYLE = { Atiti: "bg-[#EEF2FF] text-[#4F46E5]", "Booking.com": "bg-sky-50 text-sky-600", Airbnb: "bg-violet-50 text-violet-600", Direct: "bg-emerald-50 text-emerald-600" };
const ST_STYLE = {
  completed: "bg-slate-100 text-slate-500", ongoing: "bg-emerald-50 text-emerald-600",
  upcoming: "bg-amber-50 text-amber-600", checked_in: "bg-emerald-100 text-emerald-700",
  checked_out: "bg-slate-100 text-slate-500", cancelled: "bg-red-50 text-red-500", no_show: "bg-orange-50 text-orange-500",
};
const fmt = (n) => "₹" + Number(n).toLocaleString("en-IN");
const today10 = () => new Date().toISOString().slice(0, 10);
const plusDays = (d, n) => { const x = new Date(d + "T00:00:00"); x.setDate(x.getDate() + n); return x.toISOString().slice(0, 10); };

export default function ReservationsTab({ pid, refreshKey, onSeed, seeding }) {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [view, setView] = useState("all");
  const [showQuick, setShowQuick] = useState(false);
  // Batch-2 Items 5 & 7: the row-click now opens the SAME shared
  // <BookingDetailDialog> used by Stay View, which brings mandatory ID
  // verification at check-in, invoice/voucher, guest edit, dates, move and
  // payments into the Reservation tab for free. `detailId` is just the
  // booking id passed to that shared component — no bespoke drawer here.
  const [detailId, setDetailId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [qr, setQr] = useState({ unit_id: "", guest: "", mobile: "", checkin: today10(), checkout: plusDays(today10(), 1), rate: "", adults: 1, children: 0, reservation_type: "Confirm Booking" });
  const [fullForm, setFullForm] = useState(false);

  const load = useCallback(() => api.get(`/pms/properties/${pid}/reservations`).then(({ data: d }) => setData(d)).catch((e) => toast.error(errMsg(e))), [pid]);
  useEffect(() => { setData(null); setDetailId(null); load(); }, [pid, refreshKey, load]);

  if (!data) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-slate-300" size={30} /></div>;

  const { reservations: rows, today } = data;
  const arrivals = rows.filter((r) => r.checkin === today);
  const departures = rows.filter((r) => r.checkout === today);
  const inhouse = rows.filter((r) => r.checkin <= today && r.checkout > today);
  const viewRows = { all: rows, arrivals, departures, inhouse }[view];
  const filtered = viewRows.filter((r) => !q.trim() || `${r.guest} ${r.channel} ${r.unit_name} ${r.listing_title}`.toLowerCase().includes(q.toLowerCase()));

  const nights = Math.round((new Date(qr.checkout) - new Date(qr.checkin)) / 86400000);
  const qrTotal = nights > 0 ? (parseFloat(qr.rate) || 0) * nights : 0;

  const createBooking = async () => {
    setBusy(true);
    try {
      const { data: created } = await api.post(`/pms/properties/${pid}/bookings`, { ...qr, rate: parseFloat(qr.rate) || 0 });
      toast.success(`Reservation confirmed for ${qr.guest} — ${nights} night(s), ${fmt(qrTotal)}`);
      // Loud warning: booking was saved locally but NOT pushed to Channex.
      // Backend surfaces this via `channex_sync.status = 'skipped'` with a
      // reason. Show a persistent red toast so the host doesn't only find
      // out on a demo call.
      if (created?.channex_sync && created.channex_sync.status !== "queued") {
        const reason = created.channex_sync.reason;
        const label = reason === "not_linked_property"
          ? "Property not linked to OTA Connectivity"
          : reason === "not_linked_unit"
          ? "Unit isn't mapped to a room type on OTA Connectivity"
          : (created.channex_sync.message || "OTA sync skipped");
        toast.warning(`⚠ ${label} — this booking is NOT pushed to your OTA calendar. Fix it in the OTA Connectivity tab.`, {
          duration: 12000, id: `channex-skip-${created.id}`,
        });
      }
      setShowQuick(false);
      setQr({ ...qr, guest: "", mobile: "", rate: "" });
      load();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  const TabBtn = ({ id, label, count }) => (
    <button data-testid={`fd-tab-${id}`} onClick={() => setView(id)}
      className={`px-4 py-2 rounded-full text-xs font-semibold border transition-colors flex items-center gap-1.5 ${view === id ? "bg-slate-900 text-white border-transparent" : "bg-white text-slate-500 border-slate-200 hover:border-slate-400"}`}>
      {label} <span className={`px-1.5 rounded-full text-[10px] ${view === id ? "bg-white/20" : "bg-slate-100"}`}>{count}</span>
    </button>
  );

  return (
    <div className="reveal">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2 flex-wrap">
          <TabBtn id="arrivals" label="Arrivals" count={arrivals.length} />
          <TabBtn id="departures" label="Departures" count={departures.length} />
          <TabBtn id="inhouse" label="In-house" count={inhouse.length} />
          <TabBtn id="all" label="All" count={rows.length} />
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="new-reservation-button" onClick={() => setFullForm(true)}
            className="rounded-full px-5 py-2.5 text-xs font-semibold border border-slate-200 text-slate-700 hover:border-slate-400 flex items-center gap-2"><Sparkles size={13} className="brand-pink" /> Insert Transaction</button>
          <button data-testid="quick-reservation-button" onClick={() => { const t = data?.today || today10(); setQr((s) => ({ ...s, checkin: t, checkout: plusDays(t, 1) })); setShowQuick(true); }}
            className="btn-brand rounded-full px-5 py-2.5 text-xs font-semibold flex items-center gap-2"><Plus size={14} /> Quick Reservation</button>
        </div>
      </div>

      {fullForm && (
        <NewReservation pid={pid} propertyName={data?.property_name || "Property"} onClose={() => setFullForm(false)} onCreated={() => load()} pastAllowed={true} />
      )}

      <div className="mb-4"><UnassignedPanel onChanged={load} /></div>

      {rows.length === 0 ? (
        <div className="bg-[#EEF2FF] border border-[#C7D2FE] rounded-2xl px-6 py-8 text-center">
          <p className="font-display font-bold text-slate-900">No reservations yet</p>
          <button data-testid="res-seed-button" onClick={onSeed} disabled={seeding} className="btn-brand rounded-full px-5 py-2.5 text-xs font-semibold mt-4 inline-flex items-center gap-2">
            {seeding ? <Loader2 className="animate-spin" size={13} /> : <Wand2 size={13} />} Seed demo bookings
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <div className="flex items-center gap-2 px-4 md:px-5 py-3 border-b border-slate-100 bg-slate-50/60">
            <Search size={14} className="text-slate-400 shrink-0" />
            <input data-testid="res-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search guest, channel, unit…" className="w-full bg-transparent text-sm focus:outline-none min-w-0" />
            <span className="text-[11px] text-slate-400 shrink-0">{filtered.length} of {viewRows.length}</span>
          </div>
          {/* Desktop / tablet-landscape table view (≥lg) */}
          <div className="hidden lg:block overflow-x-auto max-h-[60vh] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-white z-10">
                <tr className="text-[11px] uppercase tracking-wider text-slate-400 border-b border-slate-100">
                  <th className="text-left px-5 py-3 font-semibold">Guest</th><th className="text-left px-3 py-3 font-semibold">Unit</th>
                  <th className="text-left px-3 py-3 font-semibold">Channel</th><th className="text-left px-3 py-3 font-semibold">Stay</th>
                  <th className="text-right px-3 py-3 font-semibold">Total</th><th className="text-right px-3 py-3 font-semibold">Paid</th>
                  <th className="text-right px-3 py-3 font-semibold">Balance</th><th className="text-right px-5 py-3 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.id} data-testid={`res-row-${r.id}`} onClick={() => setDetailId(r.id)}
                    className="border-b border-slate-50 hover:bg-slate-50/70 cursor-pointer">
                    <td className="px-5 py-3 font-semibold text-slate-800">{r.guest}</td>
                    <td className="px-3 py-3"><span className="h-6 px-2 rounded-md bg-slate-900 text-white font-display font-bold text-[11px] inline-flex items-center">{r.unit_name}</span></td>
                    <td className="px-3 py-3"><span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${CH_STYLE[r.channel] || "bg-slate-100 text-slate-500"}`}>{r.channel}</span></td>
                    <td className="px-3 py-3 font-mono text-xs text-slate-600">{r.checkin} → {r.checkout} <span className="text-slate-400">({r.nights}n)</span></td>
                    <td className="px-3 py-3 text-right font-mono text-slate-700">{fmt(r.amount)}</td>
                    <td className="px-3 py-3 text-right font-mono text-slate-500">{fmt(r.paid)}</td>
                    <td className={`px-3 py-3 text-right font-mono font-semibold ${r.balance > 0 ? "text-red-500" : "text-emerald-600"}`}>{fmt(r.balance)}</td>
                    <td className="px-5 py-3 text-right"><span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${ST_STYLE[r.status]}`}>{r.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Card view for narrow tablets & phones (<lg) — every row is a
              tap-large card with the same info the desktop columns show */}
          <div className="lg:hidden divide-y divide-slate-100 max-h-[70vh] overflow-y-auto">
            {filtered.map((r) => (
              <button key={r.id} data-testid={`res-card-${r.id}`} onClick={() => setDetailId(r.id)}
                className="w-full text-left px-4 py-3 hover:bg-slate-50/70 active:bg-slate-100 transition-colors">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="h-6 px-2 rounded-md bg-slate-900 text-white font-display font-bold text-[11px] inline-flex items-center shrink-0">{r.unit_name}</span>
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${CH_STYLE[r.channel] || "bg-slate-100 text-slate-500"}`}>{r.channel}</span>
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${ST_STYLE[r.status]}`}>{r.status}</span>
                    </div>
                    <div className="mt-1.5 font-semibold text-slate-900 truncate">{r.guest}</div>
                    <div className="text-[11px] font-mono text-slate-500 mt-0.5">{r.checkin} → {r.checkout} · {r.nights}n</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-mono font-semibold text-slate-800">{fmt(r.amount)}</div>
                    <div className={`text-[11px] font-mono font-semibold ${r.balance > 0 ? "text-red-500" : "text-emerald-600"}`}>{r.balance > 0 ? `Bal ${fmt(r.balance)}` : "Paid"}</div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {showQuick && (
        <NewReservation pid={pid} propertyName={data?.property_name || "Property"} onClose={() => setShowQuick(false)} onCreated={() => load()} pastAllowed={false} />
      )}

      {detailId && (
        <BookingDetailDialog
          bookingId={detailId}
          onClose={() => setDetailId(null)}
          onChanged={load}
        />
      )}
    </div>
  );
}
